import { useCallback, useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { ExternalLink, Undo2, UploadCloud, X } from 'lucide-react'
import { toast } from 'sonner'
import { DataTable } from '@/components/data-table'
import { Notice, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ConfirmAction } from '@/components/confirm-action'
import { ApiError } from '@/lib/api'
import {
  publishedFileURL,
  usePublish,
  usePublishLanguages,
  usePublishStatus,
  useRollback,
  type PublishLanguageStatus,
} from '@/features/i18n/api'

/**
 * Publish — the only screen in this module whose actions reach users.
 *
 * Everything else here edits a database nobody outside the console reads. This
 * writes files that clients fetch, which is why both actions confirm and why
 * both are audited.
 *
 * The number the screen exists for is **drift**, not coverage. Coverage is
 * answerable from the Messages screen; drift is not answerable anywhere else,
 * and the state it catches — a language 100% translated and entirely stale —
 * looks perfect from every other angle.
 */

function pct(n: number, total: number): number {
  return total === 0 ? 0 : Math.round((n / total) * 100)
}

/**
 * Versions are Unix seconds (D-1), which is right for a path segment and
 * unreadable in a table. Shown as a date with the raw value underneath, because
 * the raw value is what appears in the bucket and in `manifest.json`.
 */
function formatVersion(version: string): string {
  const seconds = Number(version)
  if (!Number.isFinite(seconds) || seconds === 0) return version
  return new Date(seconds * 1000).toISOString().replace('T', ' ').slice(0, 16) + ' UTC'
}

export function PublishPage() {
  const status = usePublishStatus()
  const query = usePublishLanguages()
  const publish = usePublish()
  const rollback = useRollback()
  const [pendingPublish, setPendingPublish] = useState<PublishLanguageStatus | null>(null)
  const [pendingRollback, setPendingRollback] = useState<PublishLanguageStatus | null>(null)
  // Only a fresh publish response carries file paths — they are computed at
  // publish time and never stored, so rollback (and every other moment) has
  // nothing to show here. This is the one place in the run that has them.
  const [justPublished, setJustPublished] = useState<{ label: string; files: string[] } | null>(
    null,
  )

  const total = status.data?.total_keys ?? 0

  // Which languages a baseline publish will drag along. Same predicate the
  // server uses: published at least once, and that published version carries
  // English for keys it has no translation for. Named in the dialog because an
  // operator cannot otherwise tell that publishing English rewrites other
  // languages' files.
  const cascadeTargets = (status.data?.languages ?? []).filter(
    (row) => !row.is_baseline && row.version !== '' && row.filled_from_en > 0,
  )

  const runPublish = useCallback(
    async (row: PublishLanguageStatus) => {
      const { baseline: result, cascaded, failed } = await publish.mutateAsync({
        code: row.language_code,
        isBaseline: row.is_baseline,
      })
      toast.success(
        `${row.display_name} published as v${result.version} — ${result.keys} keys across ${result.files.length} files, ${result.filled_from_en} shipping English.` +
          (cascaded.length > 0
            ? ` Also republished ${cascaded.map((c) => c.language_code).join(', ')}, which were serving the old English.`
            : ''),
      )
      // A partial cascade leaves English live and some languages behind it.
      // That is not a failure of the publish, so it is not thrown — but it is
      // the state an operator has to act on, so it is not a success toast either.
      if (failed?.length) {
        toast.error(
          `English is live, but ${failed
            .map((f) => f.language_code)
            .join(', ')} could not be refreshed and still serve the previous English. Publish them individually to retry.`,
          { duration: 15000 },
        )
      }
      setJustPublished({
        label:
          cascaded.length > 0
            ? `${row.display_name} + ${cascaded.length} cascaded`
            : row.display_name,
        files: [...result.files, ...cascaded.flatMap((c) => c.files)],
      })
    },
    [publish],
  )

  // The bucket is private, so this is the only way to read one of these files
  // back — a fresh, short-lived signed URL per click rather than a link saved
  // anywhere, because a saved one would just be dead by the time it was used.
  const viewFile = useCallback(async (key: string) => {
    try {
      const { url } = await publishedFileURL(key)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not open that file.')
    }
  }, [])

  const columns = useMemo<ColumnDef<PublishLanguageStatus, unknown>[]>(
    () => [
      {
        id: 'language',
        header: 'Language',
        cell: ({ row }) => (
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-[13px] font-semibold">{row.original.display_name}</span>
              {row.original.is_baseline && <Badge variant="accent">Baseline</Badge>}
            </div>
            <code className="font-mono text-[11px] text-muted-foreground">
              {row.original.language_code}
            </code>
          </div>
        ),
      },
      {
        id: 'coverage',
        header: 'Coverage',
        cell: ({ row }) => {
          const percent = pct(row.original.translated_keys, total)
          return (
            <div className="min-w-[120px]">
              <div className="text-[13px] tabular-nums">
                {row.original.translated_keys}
                <span className="text-muted-foreground">/{total}</span>{' '}
                <span className="text-[11px] text-muted-foreground">({percent}%)</span>
              </div>
              <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-secondary">
                <div
                  className={percent === 100 ? 'h-full bg-[#0f8f5f]' : 'h-full bg-[var(--primary)]'}
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          )
        },
      },
      {
        id: 'live',
        header: 'Live version',
        cell: ({ row }) =>
          row.original.version ? (
            <div className="whitespace-nowrap">
              <div className="text-[13px]">{formatVersion(row.original.version)}</div>
              <div className="text-[11px] text-muted-foreground">
                <code className="font-mono">v{row.original.version}</code>
                {row.original.published_by && ` · ${row.original.published_by}`}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {row.original.published_keys} keys
                {row.original.filled_from_en > 0 && (
                  // D-7's silence, made visible. The file is complete either
                  // way, so this is the only place the difference shows.
                  <span className="text-[#a9760c]">
                    {' · '}
                    {row.original.filled_from_en} shipping English
                  </span>
                )}
              </div>
              {row.original.recorded_version !== '' &&
                row.original.recorded_version !== row.original.version && (
                  // The manifest and the publish log disagree. Possible by
                  // construction — the manifest write is the commit point and the
                  // INSERT that records it comes after — and it used to be
                  // invisible, because this screen read the log and called it
                  // live. Nothing here is broken for clients; the history is
                  // incomplete, and a rollback is computed against the live
                  // version rather than the logged one.
                  <Badge
                    variant="warning"
                    title={`The publish log's newest entry is v${row.original.recorded_version}, but the manifest serves v${row.original.version}. Clients are fine — the history is incomplete.`}
                  >
                    log says v{row.original.recorded_version}
                  </Badge>
                )}
            </div>
          ) : (
            // Not an error state and not styled as one. Most languages sit here
            // until somebody decides they are ready.
            <span className="text-xs italic text-muted-foreground">Never published</span>
          ),
      },
      {
        id: 'drift',
        header: 'Drift',
        cell: ({ row }) => {
          if (!row.original.version) return <span className="text-muted-foreground">—</span>
          if (row.original.drift === 0) {
            return <Badge variant="success" title="Nothing has changed since this was published.">Up to date</Badge>
          }
          return (
            <Badge
              variant="warning"
              title="Keys whose English or translation changed after this version was published. Clients are still fetching the old text."
            >
              {row.original.drift} changed
            </Badge>
          )
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              disabled={publish.isPending || rollback.isPending}
              onClick={() => setPendingPublish(row.original)}
            >
              <UploadCloud className="size-3" />
              Publish
            </Button>
            <Button
              variant="ghost"
              size="sm"
              // Nothing to go back to until a second version exists — and never
              // for the baseline. Rolling English back on its own is the mirror
              // of publishing it on its own, which this screen already routes to
              // /publish/baseline: D-7 baked the English into every other
              // language's file, so moving only the baseline's manifest entries
              // leaves all 29 still serving the string being withdrawn. The API
              // refuses it; this button was offering it anyway, and there is no
              // cascading rollback to offer instead.
              disabled={
                row.original.is_baseline ||
                !row.original.can_rollback ||
                publish.isPending ||
                rollback.isPending
              }
              title={
                row.original.is_baseline
                  ? 'The baseline cannot be rolled back on its own — publish the previous English instead'
                  : row.original.can_rollback
                    ? 'Point clients back at the previous version'
                    : 'No earlier version to roll back to'
              }
              onClick={() => setPendingRollback(row.original)}
            >
              <Undo2 className="size-3" />
              Roll back
            </Button>
          </div>
        ),
      },
    ],
    [total, publish.isPending, rollback.isPending],
  )

  return (
    <>
      <PageHeader title="Publish" />

      <Notice>
        Publishing writes an <b>immutable version</b> to object storage and rewrites{' '}
        <code>manifest.json</code>, which is the commit point — files landing in the bucket change
        nothing until the manifest names them. Nothing is ever overwritten, so{' '}
        <b>rolling back moves a pointer rather than restoring anything</b>, and the version you
        left is still there afterwards.{' '}
        <b>Drift is the column to read</b>: a language can be fully translated and completely out
        of date, and this is the only screen that shows it.
      </Notice>

      <Card>
        <DataTable
          columns={columns}
          query={query}
          emptyMessage="No languages configured."
        />
      </Card>

      {justPublished && (
        <Card className="mt-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-[13px] font-semibold">Just published: {justPublished.label}</div>
              <div className="text-[11px] text-muted-foreground">
                Signed links expire in 15 minutes — this list only lasts for this session.
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setJustPublished(null)}>
              <X className="size-3" />
              Dismiss
            </Button>
          </div>
          <ul className="max-h-48 space-y-1 overflow-y-auto">
            {justPublished.files.map((key) => (
              <li key={key} className="flex items-center justify-between gap-3">
                <code className="truncate font-mono text-[11px] text-muted-foreground">{key}</code>
                <Button variant="ghost" size="sm" onClick={() => void viewFile(key)}>
                  <ExternalLink className="size-3" />
                  View
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <ConfirmAction
        open={pendingPublish !== null}
        onOpenChange={(open) => !open && setPendingPublish(null)}
        title={`Publish ${pendingPublish?.display_name}?`}
        description={
          <>
            {pendingPublish?.is_baseline ? (
              <>
                This is the <b>baseline</b>. Every untranslated key in every language is published
                carrying the English string, so publishing English changes what those keys render
                as everywhere at once.
                {cascadeTargets.length > 0 ? (
                  <>
                    {' '}
                    <b>
                      {cascadeTargets.map((r) => r.display_name).join(', ')}
                    </b>{' '}
                    {cascadeTargets.length === 1 ? 'is' : 'are'} republished automatically as part
                    of this — their live files hold English for{' '}
                    {cascadeTargets.reduce((n, r) => n + r.filled_from_en, 0)} keys between them,
                    and leaving them alone would serve the old English until someone happened to
                    publish them again.
                  </>
                ) : (
                  <> No other language is affected: every published language was fully translated.</>
                )}
                <br />
                <br />
              </>
            ) : null}
            Writes a new version and points clients at it. The current version stays in storage and
            can be rolled back to.
            {pendingPublish && pendingPublish.translated_keys < total && (
              <>
                <br />
                <br />
                <b>
                  {total - pendingPublish.translated_keys} of {total} keys have no translation
                </b>{' '}
                and will be published carrying the English string, so the files are complete and
                every client lookup succeeds. Readers see English and it looks intentional — that
                is the trade, and it is why coverage is published alongside.
              </>
            )}
          </>
        }
        confirmLabel="Publish"
        onConfirm={async () => {
          if (pendingPublish) await runPublish(pendingPublish)
        }}
        onConflict={() => void query.refetch()}
      />

      <ConfirmAction
        open={pendingRollback !== null}
        onOpenChange={(open) => !open && setPendingRollback(null)}
        title={`Roll ${pendingRollback?.display_name} back?`}
        description={
          <>
            Clients start fetching the previous version immediately. Nothing is deleted — the
            version you are leaving stays in storage, so this can be undone by rolling back again.
            <br />
            <br />
            It does <b>not</b> change the catalog. Whatever was edited since is still here, and
            still unpublished.
          </>
        }
        confirmLabel="Roll back"
        destructive
        onConfirm={async () => {
          if (!pendingRollback) return
          try {
            const result = await rollback.mutateAsync(pendingRollback.language_code)
            toast.success(`${pendingRollback.display_name} rolled back to v${result.version}.`)
          } catch (error) {
            toast.error(error instanceof ApiError ? error.message : 'Could not roll back.')
            throw error
          }
        }}
        onConflict={() => void query.refetch()}
      />
    </>
  )
}
